import { defineNuxtPlugin, onNuxtReady } from "nuxt/app";
import { useConfigurationStore } from "~/stores/configurationStore";
import { logWarn } from "~/utils/logging";

// Initialise the New Relic Browser (RUM) agent LAZILY, off the critical boot path.
//
// DIF-1479 Phase 7: previously this statically imported `@newrelic/browser-agent`
// at the top of a boot plugin, which pulled the whole agent — core + aggregators +
// session-replay recorder (~120 kB gzip across chunks per `nuxt analyze`) — into the
// initial bundle and ran it during boot. Now we (1) `import()` the agent dynamically
// so it becomes its own chunk that is NOT downloaded/parsed before first paint, and
// (2) defer init to `onNuxtReady` (after mount, during browser idle) so RUM never
// gates hydration/FCP. Config is read synchronously first (01.common-clib awaits the
// config fetch before this `03.` plugin runs), so we still skip cleanly when unset.
//
// Trade-off: errors thrown in the brief window before `onNuxtReady` fires are no
// longer captured by the agent. Accepted for the FCP/LCP win — the agent still
// captures everything from ready onward, and boot errors are also logged via
// `utils/logging`. Uses the official package (attaches to `window.newrelic`).
export default defineNuxtPlugin(() => {
    const configurationStore = useConfigurationStore();
    const newRelic = configurationStore.config?.newRelicBrowserProperties;

    if (!newRelic?.accountId || !newRelic?.applicationId || !newRelic?.licenseKey) {
        logWarn("[New Relic] Browser monitoring disabled — missing configuration");
        return;
    }

    onNuxtReady(async () => {
        const { BrowserAgent } = await import(
            "@newrelic/browser-agent/loaders/browser-agent"
        );

        const beacon = "bam.nr-data.net";

        new BrowserAgent({
            init: {
                distributed_tracing: { enabled: true },
                performance: { capture_measures: true },
                browser_consent_mode: { enabled: false },
                privacy: { cookies_enabled: true },
                ajax: {
                    deny_list: [beacon]
                }
            },
            info: {
                beacon,
                errorBeacon: beacon,
                licenseKey: newRelic.licenseKey,
                applicationID: newRelic.applicationId,
                sa: 1
            },
            loader_config: {
                accountID: newRelic.accountId,
                trustKey: newRelic.trustKey || newRelic.accountId,
                agentID: newRelic.agentId,
                licenseKey: newRelic.licenseKey,
                applicationID: newRelic.applicationId
            }
        });
    });
});
